'use client';

import { type MouseEvent, startTransition, useEffect, useState } from 'react';
import { Autocomplete } from '@/components/ui/autocomplete';
import { ExpandMoreIcon } from '@/components/ui/icons';
import { ListFilterDTO, ListFilterItemDTO } from '@/features/filters/types/filters.dto';
import {
  filterBoxBase,
  filterBoxChecked,
  filterBoxUnchecked
} from '@/features/products/components/list/filters/filterBoxStyles';
import { FilterCheckboxRow } from '@/features/products/components/list/filters/filterCheckboxRow';
import { ProductsListLocation } from '@/features/products/components/location/productsListLocation';
import { productListUpdateFilters } from '@/features/products/state/list/actions';
import {
  useProductListDispatch,
  useProductListSelectedFilters
} from '@/features/products/state/list/context';
import { listingTranslate } from '@/i18n/translations/listingTranslations';

const CityOptionRow = ({
  filter,
  selected,
  itemProps
}: {
  filter: ListFilterItemDTO;
  selected: boolean;
  itemProps: React.HTMLAttributes<HTMLLIElement>;
}) => {
  const [optimisticSelected, setOptimisticSelected] = useState(selected);

  useEffect(() => {
    setOptimisticSelected(selected);
  }, [selected]);

  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    setOptimisticSelected((previous) => !previous);
    itemProps.onClick?.(event);
  };

  return (
    <li
      {...itemProps}
      onClick={handleClick}
      className={itemProps.className ? `${itemProps.className} gap-3` : 'gap-3'}
    >
      <span
        aria-hidden="true"
        className={`${filterBoxBase} ${optimisticSelected ? filterBoxChecked : filterBoxUnchecked}`}
      />
      <span>
        {filter.name}{' '}
        {!!filter.count && (
          <span className="text-sm text-(--mui-palette-text-disabled)">({filter.count})</span>
        )}
      </span>
    </li>
  );
};

interface CityProductsFilterProps extends ListFilterDTO {
  isMobile: boolean;
  onFilterChange?: (code: string, filterCode: string) => void;
}

export const CityProductsFilter = ({
  items,
  code,
  title,
  isMobile,
  onFilterChange
}: CityProductsFilterProps) => {
  const dispatch = useProductListDispatch();
  const selectedFilters = useProductListSelectedFilters();
  const selectedCodes = selectedFilters[code] || [];
  const selectedItems = items.filter((item) => selectedCodes.includes(item.code));

  const handleFiltersChange = (filterCode: string) => {
    startTransition(() => {
      dispatch(
        productListUpdateFilters({
          filterCategoryCode: code,
          code: filterCode
        })
      );
      if (onFilterChange) {
        onFilterChange(code, filterCode);
      }
    });
  };

  if (!items.length) return null;

  return (
    <div className="flex flex-col px-4 mt-2">
      <div className="flex justify-between items-center py-2">
        <span className="font-bold">{title}</span>
        <ProductsListLocation
          openButtonText={listingTranslate('filters.map.open')}
          closeButtonText={listingTranslate('filters.map.close')}
          showCityChips={false}
        />
      </div>
      <Autocomplete
        multiple
        disablePortal
        disableClearable
        options={items}
        value={selectedItems}
        size={isMobile ? 'medium' : 'small'}
        renderTags={() => null}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        onChange={(_event, newValue) => {
          const toggled =
            newValue.length > selectedItems.length
              ? newValue.find((item) => !selectedCodes.includes(item.code))
              : selectedItems.find((item) => !newValue.some((v) => v.code === item.code));
          if (toggled) {
            handleFiltersChange(toggled.code);
          }
        }}
        popupIcon={<ExpandMoreIcon className="text-(--mui-palette-primary-main)" />}
        getOptionLabel={(filter) => filter.name}
        renderOption={(filter, { selected }, itemProps) => (
          <CityOptionRow
            key={filter.code}
            filter={filter}
            selected={selected}
            itemProps={itemProps}
          />
        )}
        label={listingTranslate('filters.city')}
        placeholder={listingTranslate('filters.localisation.placeholder')}
        className="w-full max-w-full md:max-w-100 relative"
        noOptionsText={listingTranslate('filters.no-option-text')}
      />

      <div className="gap-2 mt-3">
        {selectedItems.map((filter) => (
          <FilterCheckboxRow
            key={filter.code}
            filter={filter}
            checked={selectedCodes.includes(filter.code)}
            onToggle={handleFiltersChange}
          />
        ))}
      </div>
    </div>
  );
};
